/**
 * client.ts —— AI 通道（兼容 OpenAI 协议的第三方服务）
 *
 * 职责边界（架构原则 1：AI 与执行彻底解耦）：
 *   本文件只负责「把自然语言 + 系统提示词发给模型，拿回一段文本」。
 *   它不知道 IL 是什么，不知道宿主有哪些能力，也不碰任何执行逻辑。
 *   提示词怎么组装由 prompt.ts 决定，拿回来的文本怎么清洗由 sanitize.ts 决定。
 *
 * 为什么直接用 fetch 而不是 openai SDK：
 *   OpenAI 协议的对话补全只有一个端点、一个 JSON 请求体、一个 JSON 响应体，
 *   手写一遍只要几十行，换来的是零依赖、零 bundler 兼容坑、请求响应全程可控。
 *   PoC 没有后端，浏览器直连第三方 —— 这一点在设置页有明确提示。
 *
 * 不锁定厂商（D1）：只认 OpenAI 协议，换服务商改 BaseURL 即可。
 */

import { settingsRepo } from '@/settings/settings';
import { trace } from './trace';
import type { AISettings } from '@/types/models';

/**
 * 单次请求的超时。
 *
 * 定 120 秒的原因：翻译轮要把整份能力清单喂进去，prompt 不短；
 * 遇到服务端排队时，实测偶发超过 60 秒。太短会让用户看到莫名其妙的"超时"，
 * 而实际上请求只是慢。这个值是「宁可多等，不要误报」的取舍 ——
 * 真正的失败（网络不通、401）会立刻返回，不会卡在这里。
 */
const TIMEOUT_MS = 120_000;

/** 调用失败时抛出的错误，带一个适合直接展示给用户的中文说明 */
export class AIError extends Error {
  constructor(
    message: string,
    /** 供二轮反馈使用的简短技术原因 */
    public readonly reason: string,
  ) {
    super(message);
    this.name = 'AIError';
  }
}

/** 消息体，OpenAI 协议里的 message 结构 */
interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface ChatOptions {
  /** 覆盖 temperature（不传则用设置里的值） */
  temperature?: number;
  /** 限制返回长度，用于连通性测试这种只关心「通不通」的场景 */
  maxTokens?: number;
  /** 外部取消信号 */
  signal?: AbortSignal;
}

/**
 * 把 baseUrl 拼成完整的 chat/completions 端点。
 *
 * 用户可能填 `https://ark.cn-beijing.volces.com/api/v3`，也可能手滑填了
 * 带 `/chat/completions` 的完整地址。两种都要能跑，所以：
 *   - 末尾斜杠先去掉
 *   - 已经以 /chat/completions 结尾的，原样使用
 *   - 其余情况补上 /chat/completions
 */
export function buildEndpoint(baseUrl: string): string {
  const url = baseUrl.trim().replace(/\/+$/, '');
  return /\/chat\/completions$/.test(url) ? url : `${url}/chat/completions`;
}

/** 从服务端返回的错误体里尽力抠出一句人话 */
interface ErrorPayload {
  error?: { message?: string; code?: string; type?: string };
  message?: string;
}

/** 把 HTTP 响应体解析成 JSON，失败时返回 null（而不是抛异常） */
async function readJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text.slice(0, 300) };
  }
}

/**
 * 发起一次对话补全。
 *
 * @param systemPrompt 系统提示词（由 prompt.ts 生成，含完整能力清单）
 * @param userInput    用户的原话
 * @param extraMessages 追加的上下文，用于失败二轮（把真实错误回喂）
 */
export async function chat(
  systemPrompt: string,
  userInput: string,
  extraMessages: { role: 'user' | 'assistant'; content: string }[] = [],
): Promise<string> {
  const ai = settingsRepo.getAI();

  if (!ai.baseUrl || !ai.apiKey || !ai.model) {
    throw new AIError('AI 服务还没配置完整', '缺少服务地址、API Key 或模型名');
  }

  const messages: ChatMessage[] = [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userInput },
    ...extraMessages,
  ];

  try {
    return await requestCompletion(ai, messages, { temperature: ai.temperature });
  } catch (err) {
    throw wrapError(err);
  }
}

/**
 * 连通性测试 —— 设置页「测试连接」按钮用。
 * 发一条极短的请求，只关心能不能通。
 */
export async function testConnection(): Promise<{ ok: true; model: string; latencyMs: number }> {
  const ai = settingsRepo.getAI();
  if (!ai.baseUrl || !ai.apiKey || !ai.model) {
    throw new AIError('请先填完服务地址、API Key 和模型名', '配置不完整');
  }

  const started = performance.now();
  try {
    // 用最小的 token 消耗探活：问一个必然回一个字的问题，并把 max_tokens 压到最小。
    await requestCompletion(ai, [{ role: 'user', content: '回复「好」一个字即可' }], {
      maxTokens: 8,
    });
    const latencyMs = Math.round(performance.now() - started);
    return { ok: true, model: ai.model, latencyMs };
  } catch (err) {
    throw wrapError(err);
  }
}

/**
 * 真正发请求的地方。两个公开 API 都走这里，保证错误处理与超时行为一致。
 */
async function requestCompletion(
  ai: AISettings,
  messages: ChatMessage[],
  options: ChatOptions = {},
): Promise<string> {
  const endpoint = buildEndpoint(ai.baseUrl);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  // 外部 signal 也接进来（当前没有调用方用，但留着不亏）
  options.signal?.addEventListener('abort', () => controller.abort(), { once: true });

  const body: Record<string, unknown> = {
    model: ai.model,
    messages,
  };
  if (typeof options.temperature === 'number') body.temperature = options.temperature;
  if (typeof options.maxTokens === 'number') body.max_tokens = options.maxTokens;

  let res: Response;
  const sentAt = performance.now();
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${ai.apiKey}`,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    // fetch 本身失败 = 网络层问题，不是 HTTP 状态问题
    const name = (err as { name?: string }).name;
    if (name === 'AbortError') {
      trace.note(`HTTP 超时（${TIMEOUT_MS / 1000}s）→ ${endpoint}`);
      throw new AIError('AI 服务响应超时', `超过 ${TIMEOUT_MS / 1000} 秒没有返回`);
    }
    trace.note(`HTTP 网络失败 → ${endpoint}`, describeNetworkError(err));
    throw new AIError('连不上 AI 服务', describeNetworkError(err));
  } finally {
    clearTimeout(timer);
  }

  const payload = await readJson(res);
  // 耗时与状态码：排查「慢在哪」「是不是被限流」的第一手信息。
  // 放在请求返回之后而不是之前 —— 这样它紧挨着原始响应体，读起来连贯。
  trace.note(
    `HTTP ${res.status} · ${Math.round(performance.now() - sentAt)}ms · ${endpoint}`,
  );

  if (!res.ok) {
    const detail = extractServerMessage(payload) ?? `服务返回 ${res.status}`;
    trace.note('HTTP 错误原始响应体', payload);
    throw new AIError('AI 服务调用失败', `${detail}（${httpHint(res.status)}）`);
  }

  const text = extractContent(payload);
  if (typeof text !== 'string' || !text.trim()) {
    trace.note('响应体里没有 content', payload);
    throw new AIError('模型没有返回内容', '响应里 content 为空');
  }
  return text;
}

/** 从响应体里取出 choices[0].message.content */
function extractContent(payload: unknown): string | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined;
  const p = payload as {
    choices?: { message?: { content?: unknown } }[];
  };
  const content = p.choices?.[0]?.message?.content;
  return typeof content === 'string' ? content : undefined;
}

/** 从错误体里抠出服务端自己给的说明 */
function extractServerMessage(payload: unknown): string | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined;
  const p = payload as ErrorPayload;
  const msg = p.error?.message ?? p.message;
  return typeof msg === 'string' && msg.trim() ? msg.trim() : undefined;
}

/** 网络层错误 → 一句人话 */
function describeNetworkError(err: unknown): string {
  const raw = String(err);
  // 浏览器直连第三方最常见的两种死法：CORS 被挡、域名解析不了
  if (/Failed to fetch|NetworkError|load failed/i.test(raw)) {
    return '请求被网络层拦住了（可能是跨域限制、地址写错或断网）';
  }
  return raw;
}

/** 把内部异常统一包成 AIError */
function wrapError(err: unknown): AIError {
  if (err instanceof AIError) return err;
  if (typeof err === 'object' && err !== null) {
    const e = err as { status?: number; message?: string; code?: string };
    const hint = typeof e.status === 'number' ? httpHint(e.status) : e.code;
    const msg = e.message ?? '未知错误';
    return new AIError('AI 服务调用失败', hint ? `${msg}（${hint}）` : msg);
  }
  return new AIError('AI 服务调用失败', String(err));
}

/** HTTP 状态码 → 中文提示。这些是接第三方服务时最常撞到的几种 */
function httpHint(status: number): string {
  switch (status) {
    case 401:
      return 'API Key 无效或已过期';
    case 403:
      return '没有访问该模型的权限';
    case 404:
      return '服务地址或模型名不对';
    case 429:
      return '请求过于频繁或额度用尽';
    case 500:
    case 502:
    case 503:
      return '服务端暂时不可用';
    default:
      return `服务返回 ${status}`;
  }
}
